import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { addDays, addMonths, format, isSameDay, startOfDay } from 'date-fns';
import { ArrowLeft, CalendarDays, Mail, MapPin, Phone, User } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { ensureUserAccount } from '@/services/accountService';
import { getClientById } from '@/services/clientService';
import { getScheduledJobs } from '@/services/jobService';
import AppShell, { PageLoading } from '@/components/AppShell';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';

const FREQUENCY_LABELS = {
  one_time: 'One-Time',
  weekly: 'Weekly',
  bi_weekly: 'Bi-Weekly',
  monthly: 'Monthly',
};

const RECURRING_LABELS = {
  none: 'Does not repeat',
  daily: 'Daily',
  weekly: 'Weekly',
  'bi-weekly': 'Bi-weekly',
  monthly: 'Monthly',
};

function formatCurrency(value) {
  return Number(value || 0).toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
}

function getClientName(client) {
  return client?.customer_name || client?.property_address || 'Client';
}

function getClientServices(client) {
  const services = [];

  if (client?.services?.primaryService) {
    services.push(client.services.primaryService);
  }

  if (Array.isArray(client?.services?.addons)) {
    for (const addon of client.services.addons) {
      if (addon?.name) services.push(addon.name);
    }
  }

  return [...new Set(services.filter(Boolean))];
}

function getJobServices(job) {
  return job?.service_type
    ? job.service_type.split(',').map((service) => service.trim()).filter(Boolean)
    : [];
}

export default function ClientProfile() {
  const navigate = useNavigate();
  const { clientId } = useParams();
  const { user, loading: authLoading } = useAuth();

  const [account, setAccount] = useState(null);
  const [client, setClient] = useState(null);
  const [upcomingJobs, setUpcomingJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadData = useCallback(async () => {
    if (!user || !clientId) return;

    try {
      setLoading(true);
      setError(null);

      const result = await ensureUserAccount(user);
      const accountId = result?.account?.id;
      setAccount(result?.account || null);

      if (!accountId) {
        setClient(null);
        setUpcomingJobs([]);
        return;
      }

      const now = new Date();
      const [clientRecord, jobs] = await Promise.all([
        getClientById(accountId, clientId),
        getScheduledJobs(accountId, {
          clientId,
          startDate: now.toISOString(),
          endDate: addMonths(now, 12).toISOString(),
        }),
      ]);

      setClient(clientRecord);
      setUpcomingJobs(jobs);
    } catch (err) {
      console.error('[ClientProfile] Error loading client profile:', err);
      setError(err.message || 'Failed to load client profile');
    } finally {
      setLoading(false);
    }
  }, [clientId, user]);

  useEffect(() => {
    if (!authLoading && user) {
      loadData();
    } else if (!authLoading && !user) {
      navigate('/login');
    }
  }, [authLoading, loadData, navigate, user]);

  const nextJob = upcomingJobs[0] || null;
  const clientServices = useMemo(() => getClientServices(client), [client]);
  const calendarDays = useMemo(() => {
    const today = startOfDay(new Date());
    return Array.from({ length: 14 }, (_, index) => addDays(today, index));
  }, []);

  if (authLoading || loading) {
    return <PageLoading label="Loading client profile..." />;
  }

  if (!client && !error) {
    return (
      <AppShell
        title="Client Not Found"
        description="This client may have been removed or is not part of this account."
        accountName={account?.name}
        userLabel={user?.user_metadata?.full_name || user?.email}
      >
        <Button variant="outline" onClick={() => navigate('/clients')}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Clients
        </Button>
      </AppShell>
    );
  }

  return (
    <AppShell
      title={getClientName(client)}
      description={client?.property_address || 'Client profile'}
      accountName={account?.name}
      userLabel={user?.user_metadata?.full_name || user?.email}
      actions={
        <>
          <Button variant="outline" onClick={() => navigate('/clients')}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Clients
          </Button>
          <Button onClick={() => navigate('/schedule')} className="bg-emerald-700 hover:bg-emerald-800">
            <CalendarDays className="mr-2 h-4 w-4" />
            Schedule Service
          </Button>
        </>
      }
    >
      <div className="space-y-6">
        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <div className="grid grid-cols-1 gap-6 xl:grid-cols-[1.1fr_0.9fr]">
          <div className="space-y-6">
            <Card className="border-slate-200 shadow-sm">
              <CardHeader>
                <CardTitle>Contact Details</CardTitle>
                <CardDescription>Basic customer and property information.</CardDescription>
              </CardHeader>
              <CardContent className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <ProfileField icon={User} label="Customer" value={client.customer_name || 'No name saved'} />
                <ProfileField icon={MapPin} label="Property" value={client.property_address || 'No address saved'} />
                <ProfileField icon={Mail} label="Email" value={client.customer_email || 'No email saved'} />
                <ProfileField icon={Phone} label="Phone" value={client.customer_phone || 'No phone saved'} />
              </CardContent>
            </Card>

            <Card className="border-slate-200 shadow-sm">
              <CardHeader>
                <CardTitle>Services</CardTitle>
                <CardDescription>Work and pricing associated with this client.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-5">
                <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                  <Metric label="Frequency" value={FREQUENCY_LABELS[client.frequency] || client.frequency || 'N/A'} />
                  <Metric label="Per Visit" value={`$${formatCurrency(client.price_per_visit)}`} />
                  <Metric label="Monthly Revenue" value={`$${formatCurrency(client.estimated_monthly_revenue)}`} />
                </div>

                <div>
                  <p className="mb-2 text-sm font-medium text-slate-700">Services Performed</p>
                  {clientServices.length > 0 ? (
                    <div className="flex flex-wrap gap-2">
                      {clientServices.map((service) => (
                        <Badge key={service} variant="secondary" className="bg-emerald-50 text-emerald-800">
                          {service}
                        </Badge>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-slate-500">No services saved from the original quote yet.</p>
                  )}
                </div>

                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <Metric label="Property Type" value={client.property_type || 'N/A'} />
                  <Metric label="Measured Area" value={client.area_sq_ft ? `${formatCurrency(client.area_sq_ft)} sq ft` : 'N/A'} />
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="space-y-6">
            <Card className="border-slate-200 shadow-sm">
              <CardHeader>
                <CardTitle>Next Service</CardTitle>
                <CardDescription>The next scheduled visit for this client.</CardDescription>
              </CardHeader>
              <CardContent>
                {nextJob ? (
                  <div className="rounded-md border border-emerald-100 bg-emerald-50 p-4">
                    <p className="text-sm font-semibold text-emerald-950">
                      {format(new Date(nextJob.start_at), 'EEEE, MMM d')}
                    </p>
                    <p className="mt-1 text-2xl font-semibold text-emerald-950">
                      {format(new Date(nextJob.start_at), 'p')}
                    </p>
                    <div className="mt-3 flex flex-wrap gap-2">
                      {getJobServices(nextJob).map((service) => (
                        <Badge key={service} variant="secondary" className="bg-white text-emerald-800">
                          {service}
                        </Badge>
                      ))}
                    </div>
                    {nextJob.recurring_frequency && nextJob.recurring_frequency !== 'none' && (
                      <p className="mt-3 text-xs text-emerald-800">
                        Repeats {RECURRING_LABELS[nextJob.recurring_frequency] || nextJob.recurring_frequency}
                      </p>
                    )}
                  </div>
                ) : (
                  <div className="rounded-md border border-dashed border-slate-300 p-4 text-sm text-slate-500">
                    No upcoming service is scheduled in the next 12 months.
                  </div>
                )}
              </CardContent>
            </Card>

            <Card className="border-slate-200 shadow-sm">
              <CardHeader>
                <CardTitle>Service Calendar</CardTitle>
                <CardDescription>Upcoming visits over the next two weeks.</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-7 gap-2">
                  {calendarDays.map((day) => {
                    const dayJobs = upcomingJobs.filter((job) => isSameDay(new Date(job.start_at), day));
                    const hasJobs = dayJobs.length > 0;

                    return (
                      <div
                        key={day.toISOString()}
                        className={`min-h-20 rounded-md border p-2 ${hasJobs ? 'border-emerald-200 bg-emerald-50' : 'border-slate-200 bg-white'}`}
                      >
                        <p className="text-[11px] uppercase text-slate-500">{format(day, 'EEE')}</p>
                        <p className="text-sm font-semibold text-slate-950">{format(day, 'd')}</p>
                        {hasJobs && (
                          <div className="mt-1 space-y-1">
                            {dayJobs.slice(0, 2).map((job) => (
                              <p key={job.occurrence_key || job.id} className="truncate text-[11px] font-medium text-emerald-800">
                                {format(new Date(job.start_at), 'p')}
                              </p>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>

                {upcomingJobs.length > 0 && (
                  <div className="mt-4 space-y-2">
                    {upcomingJobs.slice(0, 5).map((job) => (
                      <div key={job.occurrence_key || job.id} className="flex items-start justify-between gap-3 rounded-md border border-slate-200 p-3">
                        <div>
                          <p className="text-sm font-medium text-slate-900">{job.service_type || 'Service visit'}</p>
                          <p className="text-xs text-slate-500">
                            {format(new Date(job.start_at), 'EEE, MMM d')} at {format(new Date(job.start_at), 'p')}
                          </p>
                        </div>
                        <Badge variant="secondary">{job.status || 'scheduled'}</Badge>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </AppShell>
  );
}

function ProfileField({ icon: Icon, label, value }) {
  return (
    <div className="rounded-md border border-slate-200 bg-white p-4">
      <div className="mb-2 flex items-center gap-2 text-sm font-medium text-slate-600">
        <Icon className="h-4 w-4 text-emerald-700" />
        {label}
      </div>
      <p className="break-words text-sm text-slate-950">{value}</p>
    </div>
  );
}

function Metric({ label, value }) {
  return (
    <div className="rounded-md border border-slate-200 bg-slate-50 p-4">
      <p className="text-xs uppercase tracking-wide text-slate-500">{label}</p>
      <p className="mt-1 text-lg font-semibold text-slate-950">{value}</p>
    </div>
  );
}
